import React, { useState } from 'react';

interface FormData {
  name: string;
  email: string;
  organization?: string;
  message: string;
}

const Contact: React.FC = () => {
  const [districtForm, setDistrictForm] = useState<FormData>({
    name: '',
    email: '',
    organization: '',
    message: '',
  });
  const [generalForm, setGeneralForm] = useState<FormData>({
    name: '',
    email: '',
    message: '',
  });
  const [districtSubmitted, setDistrictSubmitted] = useState(false);
  const [generalSubmitted, setGeneralSubmitted] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState<number | null>(null);

  const handleDistrictSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Replace with actual API call
    const contacts = JSON.parse(localStorage.getItem('districtContacts') || '[]');
    contacts.push({ ...districtForm, type: 'district', submittedAt: new Date().toISOString() });
    localStorage.setItem('districtContacts', JSON.stringify(contacts));
    setDistrictSubmitted(true);
    setDistrictForm({ name: '', email: '', organization: '', message: '' });
  };

  const handleGeneralSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Replace with actual API call
    const contacts = JSON.parse(localStorage.getItem('generalContacts') || '[]');
    contacts.push({ ...generalForm, type: 'general', submittedAt: new Date().toISOString() });
    localStorage.setItem('generalContacts', JSON.stringify(contacts));
    setGeneralSubmitted(true);
    setGeneralForm({ name: '', email: '', message: '' });
  };

  const qaItems = [
    {
      question: 'What is COUP?',
      answer: 'COUP (The CounterScript) is a tactical writing game where four factions compete for territorial control through clear writing, evidence-based arguments, and strategic counterscripts. It transforms literacy into a competitive skill by making communication the primary weapon for victory.',
    },
    {
      question: 'What grade levels can use COUP?',
      answer: 'COUP is designed for middle school through high school (grades 6-12). The game mechanics scale to different skill levels, making it adaptable for diverse learners while maintaining high expectations for all participants.',
    },
    {
      question: 'Does it work for elective classes?',
      answer: 'Absolutely. While COUP excels in English and social studies, it\'s equally effective in science (research-based arguments), fine arts (critical analysis and artist statements), and any subject where persuasive communication matters.',
    },
    {
      question: 'What devices do we need?',
      answer: 'COUP runs in any modern web browser. Students can participate on school-issued laptops, Chromebooks, or tablets. The interface is optimized for classroom projection on SmartBoards. No special hardware or downloads required.',
    },
    {
      question: 'Do students need individual accounts?',
      answer: 'No. Only the teacher or classroom council manages the account. Students participate through the shared game interface without creating personal logins. This minimizes data collection and simplifies classroom management.',
    },
    {
      question: 'What student data is stored?',
      answer: 'Minimal. We do not collect or store individual student names, images, or personal information. Game data (team submissions, scores) is associated with the classroom session, not individual students. We do not sell any data.',
    },
    {
      question: 'Can we cancel anytime?',
      answer: 'Yes. Individual subscriptions can be canceled at any time through your account dashboard. You\'ll retain access through the end of your billing period. No cancellation fees or penalties.',
    },
    {
      question: 'Is training provided?',
      answer: 'District licenses include custom onboarding and training. Individual subscriptions have access to our resource library with tutorial videos, deployment guides, and sample lesson plans.',
    },
    {
      question: 'Can classes compete against each other?',
      answer: 'Yes. COUP supports inter-class competitions within schools and inter-school tournaments. District licenses unlock additional tournament features and cross-campus analytics.',
    },
    {
      question: 'How do we get started?',
      answer: 'Individual teachers can subscribe directly through the pricing page and start immediately. Districts and schools should contact us for a custom quote and deployment plan tailored to your needs.',
    },
  ];

  return (
    <div className="min-h-screen py-16 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-6">
            Contact & Q&A
          </h1>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto">
            Questions about deployment? Need district licensing? We're here to help.
          </p>
        </div>

        {/* Contact Forms */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {/* District Contact Form */}
          <div className="card-tactical">
            <h2 className="text-2xl font-display font-bold text-white mb-2">
              District / Campus / Admin
            </h2>
            <p className="text-text-secondary mb-6 text-sm">
              For district licensing, bulk deployment, or administrative inquiries
            </p>
            {districtSubmitted ? (
              <div className="bg-scan-green bg-opacity-10 border-2 border-scan-green p-6 text-center">
                <p className="text-scan-green font-display font-semibold mb-2">
                  ✓ Message received
                </p>
                <p className="text-text-secondary text-sm">
                  We'll respond within 24-48 hours.
                </p>
                <button
                  onClick={() => setDistrictSubmitted(false)}
                  className="btn-ghost mt-4 text-sm"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleDistrictSubmit} className="space-y-4">
                <div>
                  <label htmlFor="district-name" className="block text-text-secondary mb-2 font-mono text-sm">
                    Name *
                  </label>
                  <input
                    id="district-name"
                    type="text"
                    required
                    value={districtForm.name}
                    onChange={(e) => setDistrictForm({ ...districtForm, name: e.target.value })}
                    className="input-tactical"
                  />
                </div>
                <div>
                  <label htmlFor="district-email" className="block text-text-secondary mb-2 font-mono text-sm">
                    Email *
                  </label>
                  <input
                    id="district-email"
                    type="email"
                    required
                    value={districtForm.email}
                    onChange={(e) => setDistrictForm({ ...districtForm, email: e.target.value })}
                    className="input-tactical"
                  />
                </div>
                <div>
                  <label htmlFor="district-org" className="block text-text-secondary mb-2 font-mono text-sm">
                    School / District
                  </label>
                  <input
                    id="district-org"
                    type="text"
                    value={districtForm.organization}
                    onChange={(e) => setDistrictForm({ ...districtForm, organization: e.target.value })}
                    className="input-tactical"
                  />
                </div>
                <div>
                  <label htmlFor="district-message" className="block text-text-secondary mb-2 font-mono text-sm">
                    Message *
                  </label>
                  <textarea
                    id="district-message"
                    required
                    rows={5}
                    value={districtForm.message}
                    onChange={(e) => setDistrictForm({ ...districtForm, message: e.target.value })}
                    className="input-tactical resize-none"
                  />
                </div>
                <button type="submit" className="btn-primary w-full">
                  Send Message
                </button>
              </form>
            )}
          </div>

          {/* General Contact Form */}
          <div className="card-tactical">
            <h2 className="text-2xl font-display font-bold text-white mb-2">
              Non-District / General
            </h2>
            <p className="text-text-secondary mb-6 text-sm">
              For individual teachers, questions, feedback, or general inquiries
            </p>
            {generalSubmitted ? (
              <div className="bg-scan-green bg-opacity-10 border-2 border-scan-green p-6 text-center">
                <p className="text-scan-green font-display font-semibold mb-2">
                  ✓ Message received
                </p>
                <p className="text-text-secondary text-sm">
                  We'll respond within 24-48 hours.
                </p>
                <button
                  onClick={() => setGeneralSubmitted(false)}
                  className="btn-ghost mt-4 text-sm"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleGeneralSubmit} className="space-y-4">
                <div>
                  <label htmlFor="general-name" className="block text-text-secondary mb-2 font-mono text-sm">
                    Name *
                  </label>
                  <input
                    id="general-name"
                    type="text"
                    required
                    value={generalForm.name}
                    onChange={(e) => setGeneralForm({ ...generalForm, name: e.target.value })}
                    className="input-tactical"
                  />
                </div>
                <div>
                  <label htmlFor="general-email" className="block text-text-secondary mb-2 font-mono text-sm">
                    Email *
                  </label>
                  <input
                    id="general-email"
                    type="email"
                    required
                    value={generalForm.email}
                    onChange={(e) => setGeneralForm({ ...generalForm, email: e.target.value })}
                    className="input-tactical"
                  />
                </div>
                <div>
                  <label htmlFor="general-message" className="block text-text-secondary mb-2 font-mono text-sm">
                    Message *
                  </label>
                  <textarea
                    id="general-message"
                    required
                    rows={5}
                    value={generalForm.message}
                    onChange={(e) => setGeneralForm({ ...generalForm, message: e.target.value })}
                    className="input-tactical resize-none"
                  />
                </div>
                <button type="submit" className="btn-primary w-full">
                  Send Message
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Q&A Section */}
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-display font-bold text-white mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {qaItems.map((item, index) => (
              <div key={index} className="card-tactical">
                <button
                  onClick={() => setActiveAccordion(activeAccordion === index ? null : index)}
                  className="w-full text-left flex justify-between items-center group"
                >
                  <h3 className="text-lg font-display font-semibold text-white group-hover:text-copper transition-colors pr-4">
                    {item.question}
                  </h3>
                  <svg
                    className={`w-6 h-6 text-copper transform transition-transform ${
                      activeAccordion === index ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {activeAccordion === index && (
                  <div className="mt-4 pt-4 border-t-2 border-tactical-frame">
                    <p className="text-text-secondary leading-relaxed">{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
